import mixpanelBrowser from 'mixpanel-browser'

import { DEBUG_STORAGE_KEY } from './analytics.consts'
import type { TAnalyticsStartConfig, TAnalyticsTrackingSnapshot } from './analytics.types'

/**
 * Singleton analytics client for Mixpanel integration.
 *
 * This package handles common infrastructure and flows (init, track, identify, debug),
 * while each app remains responsible for:
 * - Defining event names
 * - Providing global context
 * - Applying tracking guards (e.g., blocking impersonated users)
 *
 * @example
 * ```ts
 * import { analyticsTracker } from '@winnin/analytics'
 *
 * analyticsTracker.start({
 *   token: 'your-mixpanel-token',
 *   endpoint: 'https://wt.winnin.com',
 *   getContext: () => ({ workspaceId: '123' }),
 *   shouldTrack: () => !isImpersonating,
 * })
 *
 * analyticsTracker.track('button_clicked', { buttonName: 'export' })
 * ```
 */
export class AnalyticsClient {
  private config: TAnalyticsStartConfig | null = null
  private _debug = false

  /**
   * Initializes Mixpanel with the provided token and endpoint.
   *
   * Also automatically registers UTM parameters (first and last touch attribution)
   * from the current URL query string.
   *
   * @param config - Configuration object with Mixpanel credentials and callbacks
   */
  start(config: TAnalyticsStartConfig): void {
    this.config = config
    this._debug = window.localStorage.getItem(DEBUG_STORAGE_KEY) === 'true'

    const searchParams = new URLSearchParams(window.location.search)

    mixpanelBrowser.init(config.token, {
      api_host: config.endpoint,
      persistence: 'localStorage',
      ...(config?.shouldRecordSessions && { record_sessions_percent: 100 }),
      loaded: mp => {
        const email = searchParams.get('uniqueUserID')
        if (email) {
          const _hsq = ((window as any)._hsq = (window as any)._hsq || [])
          _hsq.push(['identify', { email }])
          mp.reset()
          mp.register({ email, createdByUniqueUserID: true })
          mp.people.set({ email, createdByUniqueUserID: true })
          mp.identify(email)
        }
      },
    })

    // UTM first/last touch tracking
    const firstTouch: Record<string, string> = {}
    const lastTouch: Record<string, string> = {}
    const utmKeys = new Set(['utm_content', 'utm_campaign', 'utm_medium', 'utm_source', 'utm_term'])

    for (const [key, value] of searchParams.entries()) {
      if (utmKeys.has(key)) {
        firstTouch[`First Touch ${key}`] = value
        lastTouch[`Last Touch ${key}`] = value
      }
    }

    mixpanelBrowser.register(lastTouch)
    mixpanelBrowser.people.set(lastTouch)

    mixpanelBrowser.register_once({ ...firstTouch, 'First URL': window.location.href })
    mixpanelBrowser.people.set_once({ ...firstTouch, 'First URL': window.location.href })
  }

  /**
   * Tracks an event with global context injected via `getContext()`.
   *
   * The context from `getContext()` is merged with the event data.
   * Event data takes precedence if there are overlapping keys.
   *
   * @param eventName - The name of the event in Mixpanel
   * @param data - Optional event properties (merged with global context)
   *
   * @example
   * ```ts
   * analyticsTracker.track('button_clicked', {
   *   buttonName: 'export_report',
   *   location: 'dashboard',
   * })
   * ```
   */
  track(eventName: string, data?: Record<string, unknown>): void {
    if (!this.canTrack()) return
    const allParams = { ...this.config?.getContext?.(), ...data }
    this.log('Event Track', eventName, allParams)
    mixpanelBrowser.track(eventName, allParams)
  }

  /**
   * Tracks an event without applying global context.
   *
   * Used for cases like login where the payload should be explicit
   * and user context isn't available yet.
   *
   * @param eventName - The name of the event in Mixpanel
   * @param data - Optional event properties (sent as-is)
   *
   * @example
   * ```ts
   * analyticsTracker.loginTrack('user_login', {
   *   method: 'email',
   *   success: true,
   * })
   * ```
   */
  loginTrack(eventName: string, data?: Record<string, unknown>): void {
    if (!this.canTrack()) return
    const allParams = { ...data }
    this.log('Login Track', eventName, allParams)
    mixpanelBrowser.track(eventName, allParams)
  }

  /**
   * Identifies the user in Mixpanel and synchronizes profile properties.
   *
   * This method:
   * 1. Calls Mixpanel's `identify()` with the user's distinct ID
   * 2. Calls `register()` to persist event properties for all future events
   * 3. Calls `people.set()` to update the user's profile in Mixpanel
   *
   * @param userId - Unique user identifier (typically email or user ID)
   * @param properties - Optional profile properties to register and sync with user profile
   *
   * @example
   * ```ts
   * analyticsTracker.identify('user@example.com', {
   *   name: 'John Doe',
   *   plan: 'enterprise',
   *   createdAt: '2024-01-01',
   * })
   * ```
   */
  identify(userId: string, properties?: Record<string, unknown>): void {
    if (!this.canTrack()) return
    const username = properties?.name as string
    this.log('User Identify', username || userId)
    if (properties) {
      mixpanelBrowser.register(properties)
      mixpanelBrowser.people.set(properties)
    }
    mixpanelBrowser.identify(userId)
  }

  /**
   * The global context `track()` merges into every event, whether or not tracking is on.
   *
   * For code that labels something with it rather than sending it — a saved file stamped with the
   * workspace and company it was made for still needs them while impersonating, when nothing is sent.
   */
  getGlobalContext(): Record<string, unknown> {
    return { ...this.config?.getContext?.() }
  }

  /**
   * Captures the project credentials, the current distinct ID and the global context, for code that
   * has to send events from somewhere `mixpanel-browser` does not run (e.g. a downloaded file).
   *
   * Returns `null` whenever `track()` would be a no-op, so the same guards apply there too.
   */
  getTrackingSnapshot(): TAnalyticsTrackingSnapshot | null {
    if (!this.config || !this.canTrack()) return null
    return {
      token: this.config.token,
      endpoint: this.config.endpoint,
      distinctId: String(mixpanelBrowser.get_distinct_id()),
      context: { ...this.config.getContext?.() },
    }
  }

  /**
   * Indicates whether debug logging is enabled.
   *
   * If `config.debug` is provided, it takes precedence.
   * Otherwise, checks the localStorage debug toggle.
   *
   * @readonly
   */
  get isDebugEnabled(): boolean {
    return this.config?.debug?.() ?? this._debug
  }

  /**
   * Toggles debug logging at runtime and persists the preference.
   *
   * When enabled, all event details are logged to the console.
   *
   * @param enabled - Whether to enable debug logging
   *
   * @example
   * ```ts
   * analyticsTracker.setDebug(true)
   * // Console output:
   * // Mixpanel Event Track
   * // ├─ Target: button_clicked
   * // └─ Params: { buttonName: 'export' }
   * ```
   */
  setDebug(enabled: boolean): void {
    this._debug = enabled
    window.localStorage.setItem(DEBUG_STORAGE_KEY, String(enabled))
  }

  private canTrack(): boolean {
    if (!this.config?.token) return false
    return this.config?.shouldTrack?.() ?? true
  }

  private log(label: string, target: string, params?: unknown): void {
    if (!this.isDebugEnabled) return
    console.group(`Mixpanel ${label}`)
    console.log('Target:', target)
    if (params) console.log('Params:', params)
    console.groupEnd()
  }
}
